import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

/**
 * Therapeutic Goals Service Class
 * Handles all therapeutic goals-related API calls and data management
 */
class TherapeuticGoalsService {
  constructor() {
    this.baseUrl = '/.netlify/functions';
  }

  /**
   * Get therapeutic goals for a patient
   * @param {Object} user - Current user
   * @param {string} patientId - Patient ID (optional - if not provided, backend uses effective patient ID)
   * @returns {Promise<Object>} Goals data
   */
  async getGoals(user, patientId = null) {
    const url = patientId 
      ? `${this.baseUrl}/get-therapeutic-goals?patientId=${patientId}`
      : `${this.baseUrl}/get-therapeutic-goals`;
    const response = await apiGet(url);

    return response;
  }

  /**
   * Create a new therapeutic goal
   * @param {Object} user - Current user
   * @param {string} patientId - Patient ID
   * @param {Object} goalData - Goal data
   * @returns {Promise<Object>} Created goal
   */
  async createGoal(user, patientId, goalData) {
    const response = await apiPost(
      `${this.baseUrl}/create-therapeutic-goal`,
      {
        patientId,
        ...goalData
      }
    );

    return response;
  }

  /**
   * Update an existing therapeutic goal
   * @param {Object} user - Current user
   * @param {string} goalId - Goal ID
   * @param {Object} goalData - Updated goal data
   * @returns {Promise<Object>} Updated goal
   */
  async updateGoal(user, goalId, goalData) {
    const response = await apiPut(
      `${this.baseUrl}/update-therapeutic-goal`,
      {
        goalId,
        ...goalData
      }
    );

    return response;
  }

  /**
   * Delete a therapeutic goal
   * @param {Object} user - Current user
   * @param {string} goalId - Goal ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteGoal(user, goalId) {
    const response = await apiDelete(
      `${this.baseUrl}/delete-therapeutic-goal`,
      { goalId }
    );

    return response;
  }

  /**
   * Validate goal data
   * @param {Object} goalData - Goal data to validate
   * @returns {boolean} Is valid
   */
  validateGoalData(goalData) {
    if (!goalData.title || !goalData.title.trim()) {
      throw new Error('Goal title is required');
    }

    if (goalData.title.trim().length < 3) {
      throw new Error('Goal title must be at least 3 characters');
    }

    if (goalData.title.trim().length > 100) {
      throw new Error('Goal title must be less than 100 characters');
    }

    if (!goalData.description || !goalData.description.trim()) {
      throw new Error('Goal description is required');
    }

    if (goalData.description.trim().length < 10) {
      throw new Error('Goal description must be at least 10 characters');
    }

    if (goalData.description.trim().length > 500) {
      throw new Error('Goal description must be less than 500 characters');
    }

    const validStatuses = ['active', 'in-progress', 'achieved'];
    if (goalData.status && !validStatuses.includes(goalData.status)) {
      throw new Error('Invalid goal status');
    }

    return true;
  }

  /**
   * Format date for display
   * @param {string|Date} dateInput - Date string or Date object
   * @returns {string} Formatted date string
   */
  formatDateForDisplay(dateInput) {
    if (!dateInput) return 'N/A';
    
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }
}

// Create singleton instance
const therapeuticGoalsService = new TherapeuticGoalsService();

export default therapeuticGoalsService;

